import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GetFilteredMessagesParams } from '@mrrmlab/api-client';
import { useMailApi } from './api-context';

export function useMailAccounts() {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', 'accounts'],
    queryFn: ({ signal }) => api.listAccounts(signal),
  });
}

export function useCreateMailAccount() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: Parameters<typeof api.createAccount>[0]) => api.createAccount(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'accounts'] }),
  });
}

export function useDeleteMailAccount() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'accounts'] }),
  });
}

export function useUpdateMailAccount() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Parameters<typeof api.updateAccount>[1] }) =>
      api.updateAccount(id, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'accounts'] }),
  });
}

export function useMailFolders(accountId: string) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', accountId, 'folders'],
    queryFn: ({ signal }) => api.listFolders(accountId, signal),
  });
}

export function useMailMessages(accountId: string, folder: string, page = 1) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', accountId, 'folders', folder, 'messages', page],
    queryFn: ({ signal }) => api.listMessages(accountId, folder, { page, limit: 30 }, signal),
  });
}

export function useMailMessage(accountId: string, folder: string, uid: number) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', accountId, 'folders', folder, 'messages', uid],
    queryFn: ({ signal }) => api.getMessage(accountId, folder, uid, signal),
  });
}

export function useArchiveMail(accountId: string, folder: string, uid: number) {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.archiveMessage(accountId, folder, uid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail', accountId, 'folders', folder, 'messages', uid] });
      qc.invalidateQueries({ queryKey: ['mail', accountId, 'folders', folder, 'messages'] });
    },
  });
}

export function useReviewQueue(limit?: number) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', 'review-queue', limit],
    queryFn: ({ signal }) => api.getReviewQueue(limit, signal),
  });
}

export function useMailTags() {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', 'tags'],
    queryFn: ({ signal }) => api.listTags(signal),
  });
}

export function useMessageTags(messageId: string | undefined) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', 'messages', messageId, 'tags'],
    queryFn: ({ signal }) => api.listMessageTags(messageId!, signal),
    enabled: !!messageId,
  });
}

export function useAssignTag() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, name, status }: { messageId: string; name: string; status?: 'suggested' | 'confirmed' }) =>
      api.assignTag(messageId, name, status),
    onSuccess: (_, { messageId }) => {
      qc.invalidateQueries({ queryKey: ['mail', 'messages', messageId, 'tags'] });
      qc.invalidateQueries({ queryKey: ['mail', 'tags'] });
    },
  });
}

export function useConfirmTag() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, tagId }: { messageId: string; tagId: string }) =>
      api.confirmTag(messageId, tagId),
    onSuccess: (_, { messageId }) =>
      qc.invalidateQueries({ queryKey: ['mail', 'messages', messageId, 'tags'] }),
  });
}

export function useRemoveMessageTag() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, tagId }: { messageId: string; tagId: string }) =>
      api.removeMessageTag(messageId, tagId),
    onSuccess: (_, { messageId }) =>
      qc.invalidateQueries({ queryKey: ['mail', 'messages', messageId, 'tags'] }),
  });
}

export function useRenameTag() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, name }: { tagId: string; name: string }) => api.renameTag(tagId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'tags'] }),
  });
}

export function useMergeTag() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceTagId, targetTagId }: { sourceTagId: string; targetTagId: string }) =>
      api.mergeTag(sourceTagId, targetTagId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'tags'] }),
  });
}

export function useDeleteTag() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => api.deleteTag(tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail', 'tags'] });
      qc.invalidateQueries({ queryKey: ['mail', 'messages'] });
    },
  });
}

export function useTagRules(tagId?: string) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', 'tag-rules', tagId ?? 'all'],
    queryFn: ({ signal }) => api.listTagRules(tagId, signal),
  });
}

export function useCreateTagRule() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.createTagRule>[0]) => api.createTagRule(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'tag-rules'] }),
  });
}

export function useDeleteTagRule() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.deleteTagRule(ruleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'tag-rules'] }),
  });
}

export function useFilteredMessages(accountId: string, folder: string, params: GetFilteredMessagesParams, enabled: boolean) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', accountId, 'folders', folder, 'filtered-messages', params],
    queryFn: ({ signal }) => api.getFilteredMessages(accountId, folder, params, signal),
    enabled,
  });
}

export function useSenderMemoryList(page = 1, limit = 30) {
  const api = useMailApi();
  return useQuery({
    queryKey: ['mail', 'sender-memory', page, limit],
    queryFn: ({ signal }) => api.listSenderMemory({ page, limit }, signal),
  });
}

export function useDeleteSenderMemoryEntry() {
  const api = useMailApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSenderMemoryEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail', 'sender-memory'] }),
  });
}

export function useReplyToMessage(accountId: string, folder: string, uid: number) {
  const api = useMailApi();
  return useMutation({
    mutationFn: (dto: { body: string; to?: string }) => api.replyToMessage(accountId, folder, uid, dto),
  });
}
